/**
 * Sorting Mode Management Mixin
 * Provides all 8 API methods for sorting mode control
 *
 * Roles: distributioncentermanager | distributioncenteruser | SuperAdmin | service
 * Base API: /api/v1/sorting/admin/mode
 */

export default {
  data: () => ({
    // Machine data
    machineCode: "",
    machines: [],
    selectedMachine: null,

    // Mode status
    currentMode: null,
    modeStatus: null,
    chuteMappings: [],
    errorChute: 19,

    // District selection for branch mode
    districts: [],
    selectedDistrictId: null,
    selectedDistrict: null,

    // Round management
    rounds: [],
    currentRound: 1,
    totalRounds: 1,

    // UI state
    isLoading: false,
    isSettingMode: false,
    isRefreshing: false,
    isSwitchingRound: false,
    isDeactivating: false,
    isDashboardLoading: false,
    isExporting: false,

    // Dashboard data
    dashboard: null,
    dashboardChutes: [],
    dashboardAlerts: [],
    dashboardStats: null,
    todayStats: null,
    scheduleReport: null, // Work-schedule split report (morning/night + total scanned)
    throughputReport: null, // Per-hour speed monitor (bills received/sorted per hour + per chute)
    flowSummary: null, // DC flow funnel (own-province branches -> DC -> out + remaining/ETA)
    showChuteDetail: false, // Collapse the wide per-chute matrix by default (space-saving)
    chutesSummary: null,

    // Frontend-only API latency badge — browser -> HAL API RTT via the public
    // no-auth probe GET /api/sorting/ping. Measures the same host the sorting
    // machine reaches (by key, not a bearer token); no auth header is sent.
    latencyMs: null,
    latencySamples: [],
    latencyStatus: "unknown", // unknown | good | ok | poor
    latencyInterval: null,
    latencyProbeMs: 3000,
    latencyGoodMs: 80,   // <= good (green)
    latencyOkMs: 200,    // <= ok (amber); above = poor (red)
    latencySampleWindow: 5,
    selectedStatsDate: new Date().toLocaleDateString('en-CA'), // Date filter for stats (format: YYYY-MM-DD)

    // Auto-refresh
    autoRefreshEnabled: true,
    autoRefreshInterval: null,
    refreshIntervalMs: 6000,
    refreshIntervalOptions: [
      { value: 3000, label: "3s" },
      { value: 4000, label: "4s" },
      { value: 5000, label: "5s" },
      { value: 6000, label: "6s" },
      { value: 8000, label: "8s" },
      { value: 10000, label: "10s" }
    ],

    // Mode options
    modeOptions: [
      { value: "province_dc", label: "ໂໝດຕ່າງແຂວງ", description: "ສຳລັບພັດສະດຸຂ້າມແຂວງ" },
      { value: "district", label: "ໂໝດເມືອງ", description: "ສຳລັບພັດສະດຸພາຍໃນແຂວງ" },
      { value: "branch", label: "ໂໝດສາຂາ", description: "ສຳລັບພັດສະດຸພາຍໃນເມືອງ" }
    ],
    selectedMode: "province_dc",

    // Error handling
    server_errors: {
      general: "",
      mode: "",
      district: "",
      round: ""
    },

    // Error messages mapping
    errorMessages: {
      "Machine not found": "ບໍ່ພົບເຄື່ອງຈັກ",
      "No DC routing configured": "ບໍ່ມີການຕັ້ງຄ່າເສັ້ນທາງສູນຄັດແຍກ",
      "Invalid mode": "ໂໝດບໍ່ຖືກຕ້ອງ",
      "District ID required for branch mode": "ຕ້ອງເລືອກເມືອງສຳລັບໂໝດສາຂາ",
      "Round not found": "ບໍ່ພົບຮອບ",
      "Unauthorized": "ບໍ່ມີສິດໃນການດຳເນີນການ"
    },

    // Success messages
    successMessages: {
      setMode: "ຕັ້ງຄ່າໂໝດສຳເລັດ",
      switchRound: "ປ່ຽນຮອບສຳເລັດ",
      refresh: "ໂຫຼດຂໍ້ມູນໃໝ່ສຳເລັດ",
      deactivate: "ປິດໃຊ້ງານໂໝດສຳເລັດ"
    },

    // Print barcode state
    printingChuteNumber: null,
    printGuidCounter: 1,

    // Clear error chute state
    clearingChuteNumber: null,

    // Active chutes error (when trying to change mode)
    activeChutesError: null,
    showActiveChutesDialog: false,

    // Pending packages error (when printing barcode)
    pendingPackagesError: null,
    showPendingPackagesDialog: false
  }),

  computed: {
    isBranchMode() {
      return this.selectedMode === "branch";
    },
    hasActiveMode() {
      return (
        this.modeStatus &&
        this.modeStatus.has_mode
      );
    },
    currentModeDisplay() {
      if (!this.currentMode) return "ບໍ່ມີໂໝດທີ່ເປີດໃຊ້ງານ";
      return this.currentMode.mode_display || this.getModeLabel(this.currentMode.mode);
    },
    canSwitchRound() {
      return this.currentMode &&
        this.currentMode.mode === "branch" &&
        this.totalRounds > 1;
    },
    /**
     * Merge chuteMappings with dashboardChutes for optimized rendering
     * Uses mapping data as fallback for destination names
     * Cached by Vue's computed property system
     */
    mergedChutes() {
      if (!this.dashboardChutes || this.dashboardChutes.length === 0) {
        return [];
      }

      // Create a lookup map for chute mappings (O(1) lookup)
      const mappingMap = {};
      if (this.chuteMappings && this.chuteMappings.length > 0) {
        this.chuteMappings.forEach(m => {
          mappingMap[m.chute_number] = m;
        });
      }

      // Merge data - mapping info as fallback for destination
      return this.dashboardChutes.map(chute => {
        const mapping = mappingMap[chute.chute_number];
        return {
          ...chute,
          // Use dashboard data first, fallback to mapping
          destination_name:
            chute.route_name ||
            chute.assigned_branch_name ||
            chute.assigned_province_dc_name ||
            (mapping ? mapping.display_name : null),
          is_machine_dc: mapping ? mapping.is_machine_dc_province : false,
          is_error_chute:
            chute.chute_number === this.errorChute || chute.is_error_chute,
        };
      });
    },
    /**
     * Morning / night blocks for the work-schedule split section.
     * Time ranges are derived from the API's start/end so they track the
     * (configurable) night-shift boundary.
     */
    scheduleBlocks() {
      const report = this.scheduleReport;
      if (!report || !report.schedules) return [];

      const emptyBlock = {
        total_scanned: 0,
        scanned_matched: 0,
        scanned_unmatched: 0,
        packages_sorted: 0,
        packages_packed: 0,
        packages_pending: 0,
        packages_sorting: 0,
        containers_created: 0,
        total_weight_kg: 0,
      };

      const range = (block) => {
        if (!block || !block.start || !block.end) return "";
        return `${block.start.slice(11, 16)} - ${block.end.slice(11, 16)}`;
      };

      const morning = report.schedules.morning || emptyBlock;
      const night = report.schedules.night || emptyBlock;

      return [
        {
          key: "morning",
          title: "ຮອບເຊົ້າ",
          icon: "fal fa-sun",
          range: range(report.schedules.morning),
          data: morning,
        },
        {
          key: "night",
          title: "ຮອບກາງຄືນ",
          icon: "fal fa-moon",
          range: range(report.schedules.night),
          data: night,
        },
      ];
    },

    /**
     * Per-hour throughput bars (received vs sorted). Bar heights are scaled to
     * the busiest hour so the tallest column fills the track.
     */
    throughputHours() {
      const report = this.throughputReport;
      if (!report || !report.hours) return [];
      const max = report.hours.reduce(
        (m, h) => Math.max(m, h.received || 0, h.sorted || 0),
        0
      ) || 1;
      return report.hours.map((h) => ({
        ...h,
        receivedPct: Math.round(((h.received || 0) / max) * 100),
        sortedPct: Math.round(((h.sorted || 0) / max) * 100),
      }));
    },

    /** Per-chute × per-hour rows for the throughput matrix (error chute last). */
    throughputChutes() {
      return (this.throughputReport && this.throughputReport.chutes) || [];
    },

    /**
     * Sorting "speed" derived from the per-hour buckets — a rate in
     * parcels/hour, the sorting analog of an internet-speed readout:
     *  - current: this hour's sorted count so far (only when viewing today)
     *  - peak:    the busiest hour's sorted count (top demonstrated rate)
     *  - avg:     average sorted per active hour
     */
    throughputRate() {
      const report = this.throughputReport;
      if (!report || !report.hours) return null;

      let peak = 0;
      let peakHour = null;
      let totalSorted = 0;
      let activeHours = 0;
      report.hours.forEach((h) => {
        const s = h.sorted || 0;
        if (s > peak) {
          peak = s;
          peakHour = h.hour;
        }
        if (s > 0 || (h.received || 0) > 0) activeHours++;
        totalSorted += s;
      });
      const avg = activeHours ? Math.round(totalSorted / activeHours) : 0;

      // "current" only makes sense while watching today's live day.
      let current = null;
      let currentHour = null;
      const today = new Date().toLocaleDateString("en-CA");
      if ((report.date || this.selectedStatsDate) === today) {
        currentHour = new Date().getHours();
        current = report.hours[currentHour]
          ? report.hours[currentHour].sorted || 0
          : 0;
      }

      return { peak, peakHour, avg, current, currentHour, totalSorted, activeHours };
    },

    /** Day totals for the throughput summary line. */
    throughputTotals() {
      return (this.throughputReport && this.throughputReport.totals) || null;
    },

    /**
     * Column totals for the per-chute matrix footer: sorted count per hour
     * summed over the displayed chute rows + the grand total, so the footer
     * always reconciles with the table above it.
     */
    throughputChuteHourTotals() {
      const chutes = this.throughputChutes;
      if (!chutes.length) return null;

      const hourly = Array(24).fill(0);
      let total = 0;
      chutes.forEach((c) => {
        (c.hourly || []).forEach((n, i) => {
          hourly[i] += n || 0;
        });
        total += c.total || 0;
      });
      return { hourly, total };
    },

    /** Error chute number reported by the throughput endpoint (fallback 19). */
    throughputErrorChute() {
      return (this.throughputReport && this.throughputReport.error_chute) || this.errorChute;
    },

    /**
     * Server-side health for the day (needs the duration/confirm backend):
     *  - confirmGap: sorts assigned via getapi666 the machine never confirmed
     *    via getapi888 (the stuck-"sorting" backlog forming today)
     *  - avgMs/maxMs/slowCount: getapi666 server response time; slowCount =
     *    scans over the SLA threshold (server default 1000 ms)
     * Null when the backend doesn't return the fields yet (old API).
     */
    throughputHealth() {
      const t = this.throughputTotals;
      if (!t || t.confirmed === undefined) return null;
      return {
        confirmed: t.confirmed || 0,
        confirmGap: t.confirm_gap || 0,
        avgMs: t.avg_ms !== null && t.avg_ms !== undefined ? t.avg_ms : null,
        maxMs: t.max_ms !== null && t.max_ms !== undefined ? t.max_ms : null,
        slowCount: t.slow_count || 0,
        slowThresholdMs: this.throughputReport.slow_threshold_ms || 1000,
      };
    },

    /**
     * DC flow funnel rows (from /flow-summary): parcels in transit from the
     * DC's own-province branches, inbounded at the DC (unpacked / packed) and
     * outbounded today. Each row shows the total plus the ຮອບເຊົ້າ/ຮອບກາງຄືນ
     * split and the old-backlog carryover. Self-hides on an old backend
     * (flowSummary stays null).
     */
    flowRows() {
      const f = this.flowSummary && this.flowSummary.flow;
      if (!f) return [];

      const origin = this.flowSummary.origin || {};
      const provinceName = origin.province_name || "ແຂວງຂອງສູນ";

      const row = (key, label, icon, state, accent, scope, hint) => ({
        key,
        label,
        icon,
        accent: accent || "",
        scope: scope || "",
        hint: hint || "",
        total: (state && state.total && state.total.parcels) || 0,
        shipments: (state && state.total && state.total.shipments) || 0,
        morning:
          (state && state.schedules && state.schedules.morning.parcels) || 0,
        night:
          (state && state.schedules && state.schedules.night.parcels) || 0,
        // null = day-bounded metric (outbounded) with no carryover concept
        carryover:
          state && state.carryover ? state.carryover.parcels || 0 : null,
      });

      const rows = [
        row(
          "un_inbound",
          "ກຳລັງສົ່ງມາສູນ (ຍັງບໍ່ເຂົ້າ)",
          "fal fa-truck",
          f.un_inbound,
          "wait",
          // This row only counts parcels sent by the DC's own-province
          // branches — make that visible so nobody reads this as ALL inbound.
          "ສະເພາະ" + provinceName,
          "ນັບສະເພາະພັດສະດຸທີ່ສາຂາພາຍໃນ " +
            provinceName +
            " (" +
            (origin.branch_count || 0) +
            " ສາຂາ) ສົ່ງອອກມາຫາສູນ — ພັດສະດຸຈາກແຂວງອື່ນຢູ່ແຖວ ສູນ→ສູນ"
        ),
      ];

      // Cross-province incoming (other DCs -> this DC trunk legs PLUS other
      // provinces' branches sending straight to this hub). Absent on an old
      // backend. NOT day-bounded: far provinces take 2+ days, so departures
      // before today keep showing in ຄ້າງເກົ່າ until they arrive — and the
      // backend keeps them out of remaining/ETA on purpose.
      if (f.un_inbound_dc) {
        rows.push(
          row(
            "un_inbound_dc",
            "ສົ່ງມາຈາກແຂວງອື່ນ",
            "fal fa-truck-clock",
            f.un_inbound_dc,
            "wait",
            "ຂ້າມແຂວງ",
            "ພັດສະດຸຈາກແຂວງອື່ນທີ່ກຳລັງສົ່ງມາຫາສູນນີ້ ແລະ ຍັງບໍ່ທັນເຂົ້າ — " +
              "ນັບທັງສູນຄັດແຍກແຂວງອື່ນ (ສູນ→ສູນ) ແລະ ສາຂາແຂວງອື່ນທີ່ສົ່ງກົງມາສູນ. " +
              "ເສັ້ນທາງໄກອາດໃຊ້ເວລາ 2+ ມື້, ຈຳນວນທີ່ອອກກ່ອນມື້ນີ້ສະແດງຢູ່ ຄ້າງເກົ່າ " +
              "(ບໍ່ນັບເຂົ້າ ຍັງເຫຼືອຕ້ອງຄັດແຍກ/ETA)"
          )
        );
      }

      rows.push(
        row("at_dc", "ເຂົ້າສູນແລ້ວ (ລໍຖ້າຄັດແຍກ)", "fal fa-warehouse", f.at_dc, "busy"),
        row("at_dc_packed", "ແພັກແລ້ວ ລໍຖ້າສົ່ງອອກ", "fal fa-box-taped", f.at_dc_packed, ""),
        row("outbounded", "ອອກຈາກສູນແລ້ວ (ມື້ນີ້)", "fal fa-truck-fast", f.outbounded, "done")
      );

      return rows;
    },

    flowRemaining() {
      return (this.flowSummary && this.flowSummary.remaining) || null;
    },

    /** ETA block: remaining-to-sort at today's scan rate (null = no scans yet). */
    flowEta() {
      const eta = this.flowSummary && this.flowSummary.eta;
      if (!eta) return null;
      return {
        rate: eta.scan_rate_per_hour,
        remaining: eta.remaining_to_sort,
        hours: eta.estimated_hours,
        finishAt: eta.estimated_finish_at
          ? eta.estimated_finish_at.slice(11, 16)
          : null,
      };
    },

    /** CSS class + label for the latency badge. */
    latencyClass() {
      return `latency-${this.latencyStatus}`;
    },
    latencyLabel() {
      const labels = { good: "ດີ", ok: "ພໍໃຊ້", poor: "ຊ້າ", unknown: "—" };
      return labels[this.latencyStatus] || "—";
    },
  },

  methods: {
    /** Thousand-separated display for dashboard counts (1234567 -> 1,234,567). */
    formatNumber(value) {
      return (Number(value) || 0).toLocaleString("en-US");
    },

    /**
     * Get human-readable mode label
     */
    getModeLabel(mode) {
      const option = this.modeOptions.find(opt => opt.value === mode);
      return option ? option.label : mode;
    },

    /**
     * Clear all errors
     */
    clearErrors() {
      this.server_errors = {
        general: "",
        mode: "",
        district: "",
        round: ""
      };
    },

    /**
     * Handle API error response
     */
    handleError(error, errorField = "general") {
      console.log(error);
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 422) {
          // Validation error
          if (data.errors) {
            const firstError = Object.values(data.errors)[0];
            this.server_errors[errorField] = Array.isArray(firstError) ? firstError[0] : firstError;
          } else if (data.message) {
            this.server_errors[errorField] = this.errorMessages[data.message] || data.message;
          }
        } else if (status === 403) {
          this.server_errors[errorField] = this.errorMessages["Unauthorized"];
        } else if (status === 404) {
          this.server_errors[errorField] = this.errorMessages["Machine not found"];
        } else if (data && data.error) {
          this.server_errors[errorField] = this.errorMessages[data.error] || data.error;
        } else {
          this.server_errors[errorField] = "ເກີດຂໍ້ຜິດພາດ, ກະລຸນາລອງໃໝ່";
        }
      } else {
        this.server_errors[errorField] = "ບໍ່ສາມາດເຊື່ອມຕໍ່ກັບເຊີເວີ";
      }
    },

    // =====================================================
    // API 1: Set Mode - POST /api/v1/sorting/admin/mode/set
    // =====================================================
    async setMode(mode = null, districtId = null, round = null) {
      if (!this.machineCode) {
        this.server_errors.mode = "ກະລຸນາເລືອກເຄື່ອງຈັກ";
        return false;
      }

      const modeToSet = mode || this.selectedMode;

      // Validate district for branch mode
      if (modeToSet === "branch" && !districtId && !this.selectedDistrictId) {
        this.server_errors.district = "ກະລຸນາເລືອກເມືອງສຳລັບໂໝດສາຂາ";
        return false;
      }

      this.clearErrors();
      this.isSettingMode = true;

      try {
        const payload = {
          machine_code: this.machineCode,
          mode: modeToSet
        };

        if (modeToSet === "branch") {
          payload.district_id = districtId || this.selectedDistrictId;
          payload.round_number = round || this.currentRound || 1;
        }

        const response = await this.$http.post("sorting/admin/mode/set", payload);

        if (response.data.success) {
          const data = response.data.data;
          this.currentMode = {
            ...data.mode,
            mode: modeToSet,
            mode_display: this.getModeLabel(modeToSet)
          };
          this.chuteMappings = data.mappings || [];
          this.errorChute = data.error_chute || 19;
          this.totalRounds = data.total_rounds || 1;
          this.currentRound = data.mode.current_round || 1;

          // Clear any previous active chutes error
          this.activeChutesError = null;

          this.$notification.OpenNotification_AddItem_OnSuccess("top-right", "dark", 3000);

          // Refresh status
          await this.getModeStatus();
          return true;
        } else {
          // Handle active chutes error - check multiple response formats
          const activeChutes = this.extractActiveChutes(response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
          } else if (response.data.error) {
            this.server_errors.mode = response.data.error;
          } else if (response.data.message && typeof response.data.message === "string") {
            this.server_errors.mode = response.data.message;
          }
          return false;
        }
      } catch (error) {
        // Handle error response with active chutes
        if (error.response && error.response.data) {
          const activeChutes = this.extractActiveChutes(error.response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
            return false;
          }
        }
        this.handleError(error, "mode");
        return false;
      } finally {
        this.isSettingMode = false;
      }
    },

    // =====================================================
    // API 2: Get Current Mode - GET /api/v1/sorting/admin/mode/current/{machineCode}
    // =====================================================
    async getCurrentMode() {
      if (!this.machineCode) return;

      this.isLoading = true;
      this.clearErrors();

      try {
        const response = await this.$http.get("sorting/admin/mode/current", {
          params: { machine_code: this.machineCode }
        });
        const mainData = response.data;
        if (mainData.success && mainData.has_mode) {
          const data = mainData.data;
          this.currentMode = {
            mode: data.mode,
            mode_display: data.mode_display,
            status_display: data.status_display,
            district_id: data.district_id,
            district_name: data.district_name,
            current_round: data.current_round,
            total_rounds: data.total_rounds,
            activated_at: data.activated_at,
          };
          this.chuteMappings = data.mappings || [];
          this.errorChute = data.error_chute || 19;
          this.totalRounds = data.total_rounds || 1;
          this.currentRound = data.current_round || 1;
          this.selectedDistrictId = data.district_id;
          // Sync selected mode with current mode
          if (data.mode) {
            this.selectedMode = data.mode;
          }
          // Fetch rounds if branch mode is active
          if (data.mode === "branch" && data.district_id) {
            await this.getRounds(data.district_id);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // No mode set - this is OK
          this.currentMode = null;
          this.chuteMappings = [];
        } else {
          this.handleError(error, "general");
        }
      } finally {
        this.isLoading = false;
      }
    },

    // =====================================================
    // API 3: Switch Round - POST /api/v1/sorting/admin/mode/switch-round
    // =====================================================
    async switchRound(round) {
      if (!this.machineCode) {
        this.server_errors.round = "ກະລຸນາເລືອກເຄື່ອງຈັກ";
        return false;
      }

      this.clearErrors();
      this.isSwitchingRound = true;

      try {
        const response = await this.$http.post(
          "sorting/admin/mode/switch-round",
          {
            machine_code: this.machineCode,
            round_number: round,
          }
        );

        if (response.data.success) {
          this.currentRound = round;
          this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);

          // Refresh current mode to get updated mappings
          await this.getCurrentMode();
          return true;
        } else {
          // Handle active chutes error
          const activeChutes = this.extractActiveChutes(response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
          } else if (response.data.message) {
            this.server_errors.round = typeof response.data.message === "string"
              ? response.data.message
              : response.data.message.message || "ເກີດຂໍ້ຜິດພາດ";
          }
          return false;
        }
      } catch (error) {
        // Handle error response with active chutes
        if (error.response && error.response.data) {
          const activeChutes = this.extractActiveChutes(error.response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
            return false;
          }
        }
        this.handleError(error, "round");
        return false;
      } finally {
        this.isSwitchingRound = false;
      }
    },

    // =====================================================
    // API 4: Refresh Mappings - POST /api/v1/sorting/admin/mode/refresh
    // =====================================================
    async refreshMappings() {
      if (!this.machineCode) {
        this.server_errors.general = "ກະລຸນາເລືອກເຄື່ອງຈັກ";
        return false;
      }

      this.clearErrors();
      this.isRefreshing = true;

      try {
        const response = await this.$http.post("sorting/admin/mode/refresh-mappings", {
          machine_code: this.machineCode
        });

        if (response.data.success) {
          this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);

          // Refresh current mode to get updated mappings
          await this.getCurrentMode();
          return true;
        }
        return false;
      } catch (error) {
        this.handleError(error, "general");
        return false;
      } finally {
        this.isRefreshing = false;
      }
    },

    // =====================================================
    // API 5: Get Districts - GET /api/v1/sorting/admin/mode/districts/{machineCode}
    // =====================================================
    async getDistricts() {
      if (!this.machineCode) return;

      this.isLoading = true;

      try {
        const response = await this.$http.get("sorting/admin/mode/districts", {
          params: { machine_code: this.machineCode }
        });

        if (response.data.success) {
          this.districts = response.data.data.districts || [];
        }
      } catch (error) {
        this.handleError(error, "district");
        this.districts = [];
      } finally {
        this.isLoading = false;
      }
    },

    // =====================================================
    // API 6: Get Rounds - GET /api/v1/sorting/admin/mode/rounds/{machineCode}/{districtId}
    // =====================================================
    async getRounds(districtId = null) {
      const district = districtId || this.selectedDistrictId;
      if (!this.machineCode || !district) return;

      // Don't use isLoading here to avoid hiding the whole UI
      try {
        const response = await this.$http.get("sorting/admin/mode/rounds", {
          params: {
            machine_code: this.machineCode,
            district_id: district
          }
        });

        if (response.data.success) {
          const data = response.data.data;
          this.rounds = data.rounds || [];
          this.totalRounds = data.total_rounds || 1;
          // Auto-select first round if not already selected
          if (this.rounds.length > 0 && !this.currentRound) {
            this.currentRound = this.rounds[0].round;
          }
        }
      } catch (error) {
        this.handleError(error, "round");
        this.rounds = [];
      }
    },

    // =====================================================
    // API 7: Get Mode Status - GET /api/v1/sorting/admin/mode/status/{machineCode}
    // =====================================================
    async getModeStatus() {
      if (!this.machineCode) return;

      try {
        const response = await this.$http.get("sorting/admin/mode/status", {
          params: { machine_code: this.machineCode }
        });
        if (response.data.success) {
          this.modeStatus = response.data.data;
        }
      } catch (error) {
        this.modeStatus = null;
      }
    },

    // =====================================================
    // API 8: Deactivate Mode - POST /api/v1/sorting/admin/mode/deactivate
    // =====================================================
    async deactivateMode() {
      if (!this.machineCode) {
        this.server_errors.general = "ກະລຸນາເລືອກເຄື່ອງຈັກ";
        return false;
      }

      this.clearErrors();
      this.isDeactivating = true;

      try {
        const response = await this.$http.post("sorting/admin/mode/deactivate", {
          machine_code: this.machineCode
        });

        if (response.data.success) {
          this.currentMode = null;
          this.chuteMappings = [];
          this.modeStatus = { has_mode: false, is_active: false };

          this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);
          return true;
        } else {
          // Handle active chutes error
          const activeChutes = this.extractActiveChutes(response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
          } else if (response.data.message) {
            this.server_errors.general = typeof response.data.message === "string"
              ? response.data.message
              : response.data.message.message || "ເກີດຂໍ້ຜິດພາດ";
          }
          return false;
        }
      } catch (error) {
        // Handle error response with active chutes
        if (error.response && error.response.data) {
          const activeChutes = this.extractActiveChutes(error.response.data);
          if (activeChutes) {
            this.activeChutesError = activeChutes;
            this.showActiveChutesDialog = true;
            return false;
          }
        }
        this.handleError(error, "general");
        return false;
      } finally {
        this.isDeactivating = false;
      }
    },

    // =====================================================
    // Helper: Load all data for selected machine
    // =====================================================
    async loadMachineData() {
      if (!this.machineCode) return;

      this.isLoading = true;
      this.clearErrors();

      try {
        // Load in parallel
        await Promise.all([
          this.getModeStatus(),
          this.getCurrentMode(),
          this.getDistricts(),
          this.fetchDashboard(),
          this.fetchScheduleReport(),
          this.fetchThroughput(),
          this.fetchFlowSummary()
        ]);

        // Start auto-refresh for dashboard
        this.startAutoRefresh();
        // Start the frontend-only API latency probe
        this.startLatencyProbe();
      } finally {
        this.isLoading = false;
      }
    },

    // =====================================================
    // Helper: Fetch machines list
    // GET /api/sorting/admin/machines?is_active=true&find_by_user=true
    // =====================================================
    async fetchMachines() {
      try {
        const response = await this.$http.get("sorting/admin/machines", {
          params: {
            is_active: true,
            find_by_user: true
          }
        });
        if (response.data && response.data.success) {
          this.machines = response.data.data || [];
          // Auto-select first machine if available
          if (this.machines.length > 0) {
            this.machineCode = this.machines[0].machine_code;
            this.selectedMachine = this.machines[0];
            await this.loadMachineData();
          }
        } else {
          this.machines = [];
        }
      } catch (error) {
        this.machines = [];
      }
    },

    /**
     * Select a machine and load its data
     */
    async selectMachine(machine) {
      this.selectedMachine = machine;
      this.machineCode = machine.machine_code;
      await this.loadMachineData();
    },

    /**
     * Handle district selection for branch mode
     */
    async onDistrictSelected(district) {
      this.selectedDistrict = district;
      this.selectedDistrictId = district.id;
      await this.getRounds(district.id);
    },

    // =====================================================
    // Dashboard API
    // GET /api/sorting/dashboard/machines/{machineCode}/dashboard
    // =====================================================
    async fetchDashboard() {
      if (!this.machineCode) return;

      this.isDashboardLoading = true;

      try {
        const params = {};
        if (this.selectedStatsDate) {
          params.date = this.selectedStatsDate;
        }
        const response = await this.$http.get(`sorting/dashboard/machines/${this.machineCode}/dashboard`, { params });

        if (response.data && response.data.success) {
          const data = response.data.data;

          // Use Object.freeze() to prevent Vue reactivity overhead
          // These are read-only data that don't need deep watching
          const chutes = data.chutes?.chutes || [];
          const alerts = data.alerts || [];

          // Freeze arrays to prevent Vue from adding getters/setters to each item
          this.dashboardChutes = Object.freeze(chutes);
          this.dashboardAlerts = Object.freeze(alerts);

          // These are small objects, freeze them too
          this.chutesSummary = data.chutes?.summary || data.machine?.chutes_summary || null;
          this.todayStats = data.machine?.today_stats || null;
          this.dashboardStats = data.stats || null;

          // Don't store full dashboard object to reduce memory
          this.dashboard = null;
        }
      } catch (error) {
        console.error("Failed to fetch dashboard:", error);
      } finally {
        this.isDashboardLoading = false;
      }
    },

    // =====================================================
    // Work-schedule split report + total scanned (getapi666)
    // GET /api/sorting/dashboard/machines/{machineCode}/schedule-report
    // =====================================================
    async fetchScheduleReport() {
      if (!this.machineCode) return;

      try {
        const params = {};
        if (this.selectedStatsDate) {
          params.date = this.selectedStatsDate;
        }
        const response = await this.$http.get(
          `sorting/dashboard/machines/${this.machineCode}/schedule-report`,
          { params }
        );

        if (response.data && response.data.success) {
          this.scheduleReport = Object.freeze(response.data.data || null);
        }
      } catch (error) {
        console.error("Failed to fetch schedule report:", error);
      }
    },

    // =====================================================
    // Per-hour throughput/speed monitor
    // GET /api/sorting/dashboard/machines/{machineCode}/throughput
    // =====================================================
    async fetchThroughput() {
      if (!this.machineCode) return;

      try {
        const params = {};
        if (this.selectedStatsDate) {
          params.date = this.selectedStatsDate;
        }
        const response = await this.$http.get(
          `sorting/dashboard/machines/${this.machineCode}/throughput`,
          { params }
        );

        if (response.data && response.data.success) {
          this.throughputReport = Object.freeze(response.data.data || null);
        }
      } catch (error) {
        console.error("Failed to fetch throughput:", error);
      }
    },

    // =====================================================
    // DC flow summary (workload funnel + remaining/ETA)
    // GET /api/sorting/dashboard/machines/{machineCode}/flow-summary
    // =====================================================
    async fetchFlowSummary() {
      if (!this.machineCode) return;

      try {
        const params = {};
        if (this.selectedStatsDate) {
          params.date = this.selectedStatsDate;
        }
        const response = await this.$http.get(
          `sorting/dashboard/machines/${this.machineCode}/flow-summary`,
          { params }
        );

        if (response.data && response.data.success) {
          this.flowSummary = Object.freeze(response.data.data || null);
        }
      } catch (error) {
        // Old backend (404) or transient error — the card just self-hides.
        console.error("Failed to fetch flow summary:", error);
      }
    },

    // =====================================================
    // Frontend-only API latency badge (browser -> HAL API RTT)
    // Times a bare fetch to the public no-auth probe /api/sorting/ping.
    // =====================================================
    latencyPingUrl() {
      const base =
        (this.$http && this.$http.defaults && this.$http.defaults.baseURL) || "";
      return base.replace(/\/+$/, "") + "/sorting/ping";
    },

    async measureLatency() {
      const url = this.latencyPingUrl() + "?t=" + Date.now();
      const clock = () =>
        window.performance && performance.now ? performance.now() : Date.now();
      const start = clock();
      try {
        // Bare fetch, NO Authorization header (the probe is public) so this is
        // a clean network RTT to the API host and a simple CORS request.
        await fetch(url, { method: "GET", cache: "no-store", mode: "cors" });
        const rtt = Math.round(clock() - start);

        const samples = this.latencySamples
          .concat(rtt)
          .slice(-this.latencySampleWindow);
        this.latencySamples = samples;

        // Rolling median smooths the first-sample handshake spike.
        const sorted = samples.slice().sort((a, b) => a - b);
        this.latencyMs = sorted[Math.floor(sorted.length / 2)];

        if (this.latencyMs <= this.latencyGoodMs) this.latencyStatus = "good";
        else if (this.latencyMs <= this.latencyOkMs) this.latencyStatus = "ok";
        else this.latencyStatus = "poor";
      } catch (e) {
        // Network failure = worst case; reset the window so recovery is honest.
        this.latencyMs = null;
        this.latencyStatus = "poor";
        this.latencySamples = [];
      }
    },

    startLatencyProbe() {
      if (this.latencyInterval) return;
      this.measureLatency();
      this.latencyInterval = setInterval(
        () => this.measureLatency(),
        this.latencyProbeMs
      );
    },

    stopLatencyProbe() {
      if (this.latencyInterval) {
        clearInterval(this.latencyInterval);
        this.latencyInterval = null;
      }
    },

    /** Expand/collapse the wide per-chute × per-hour throughput matrix. */
    toggleChuteDetail() {
      this.showChuteDetail = !this.showChuteDetail;
    },

    /**
     * Export the work-schedule report to Excel (summary + per-schedule bills).
     * Downloads via an authenticated blob request for the selected date.
     */
    async exportScheduleReport() {
      if (!this.machineCode || this.isExporting) return;

      this.isExporting = true;
      try {
        const params = {};
        if (this.selectedStatsDate) {
          params.date = this.selectedStatsDate;
        }
        const res = await this.$http.get(
          `sorting/dashboard/machines/${this.machineCode}/schedule-report/export`,
          { params, responseType: "blob" }
        );

        const dateStr =
          this.selectedStatsDate || new Date().toLocaleDateString("en-CA");
        const fileName = `sorting_schedule_report_${this.machineCode}_${dateStr}.xlsx`;

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export schedule report failed:", error);
        if (this.$notification && this.$notification.error) {
          this.$notification.error({ message: "ບໍ່ສາມາດສົ່ງອອກ Excel ໄດ້" });
        }
      } finally {
        this.isExporting = false;
      }
    },

    /**
     * Start auto-refresh for dashboard
     */
    startAutoRefresh() {
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
      }
      this.autoRefreshEnabled = true;
      this.autoRefreshInterval = setInterval(() => {
        if (this.autoRefreshEnabled && this.machineCode) {
          this.fetchDashboard();
          this.fetchScheduleReport();
          this.fetchThroughput();
          this.fetchFlowSummary();
        }
      }, this.refreshIntervalMs);
    },

    /**
     * Stop auto-refresh for dashboard
     */
    stopAutoRefresh() {
      this.autoRefreshEnabled = false;
      if (this.autoRefreshInterval) {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },

    /**
     * Toggle auto-refresh
     */
    toggleAutoRefresh() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
      } else {
        this.startAutoRefresh();
      }
    },

    /**
     * Change refresh interval
     */
    changeRefreshInterval(intervalMs) {
      this.refreshIntervalMs = intervalMs;
      // Restart auto-refresh if enabled
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
      }
    },

    /**
     * Get status color class for chute
     */
    getChuteStatusClass(status) {
      const statusClasses = {
        "empty": "chute-empty",
        "active": "chute-active",
        "full": "chute-full",
        "packing": "chute-packing",
        "error": "chute-error"
      };
      return statusClasses[status] || "chute-empty";
    },

    /**
     * Get status label for chute
     */
    getChuteStatusLabel(status) {
      const statusLabels = {
        "empty": "ວ່າງ",
        "active": "ກຳລັງໃຊ້ງານ",
        "full": "ເຕັມ",
        "packing": "ກຳລັງແພັກ",
        "error": "ຕ້ອງກວດສອບ"
      };
      return statusLabels[status] || status;
    },

    /**
     * Handle stats date change
     */
    onStatsDateChange() {
      this.fetchDashboard();
      this.fetchScheduleReport();
      this.fetchThroughput();
      this.fetchFlowSummary();
    },

    /**
     * Format date for API (YYYY-MM-DD)
     */
    formatDateForApi(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    /**
     * Get alert severity class
     */
    getAlertSeverityClass(severity) {
      const severityClasses = {
        "info": "alert-info",
        "warning": "alert-warning",
        "error": "alert-error",
        "critical": "alert-critical"
      };
      return severityClasses[severity] || "alert-info";
    },

    /**
     * Generate GUID for box barcode
     * Format: MACHINECODE-YYYYMMDD-HHmmssSSS-XXX
     */
    generateBoxGuid() {
      const now = new Date();
      const dateStr = now.getFullYear().toString() +
        String(now.getMonth() + 1).padStart(2, "0") +
        String(now.getDate()).padStart(2, "0");
      const timeStr = String(now.getHours()).padStart(2, "0") +
        String(now.getMinutes()).padStart(2, "0") +
        String(now.getSeconds()).padStart(2, "0") +
        String(now.getMilliseconds()).padStart(3, "0");
      const counter = String(this.printGuidCounter++).padStart(3, "0");
      return `${this.machineCode}-${dateStr}-${timeStr}-${counter}`;
    },

    /**
     * Print box barcode for a chute
     * POST /api/sorting/admin/box-barcode
     */
    async printBoxBarcode(chuteNumber) {
      if (!this.machineCode || this.printingChuteNumber !== null) return;

      this.printingChuteNumber = chuteNumber;

      try {
        const guid = this.generateBoxGuid();
        const payload = {
          guid: guid,
          machineCode: this.machineCode,
          operator: "",
          chuteCode: chuteNumber, // Without zero padding
          containerBarcode: ""
        };

        const response = await this.$http.post("sorting/admin/box-barcode", payload);

        if (response.data && response.data.code === 200 && typeof response.data.message === "string") {
          // Print the base64 PDF
          this.printBase64Pdf(response.data.message);
        } else {
          this.handlePrintBarcodeError(response, null, chuteNumber);
        }
      } catch (error) {
        console.error("Print barcode error:", error);
        this.handlePrintBarcodeError(null, error, chuteNumber);
      } finally {
        this.printingChuteNumber = null;
      }
    },

    /**
     * Print base64 PDF in browser
     */
    printBase64Pdf(base64String) {
      try {
        // Convert base64 to blob
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create object URL and open in new window for printing
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, "_blank");

        if (printWindow) {
          printWindow.onload = function() {
            printWindow.print();
            // Clean up after printing
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 1000);
          };
        } else {
          // Fallback: download the PDF if popup blocked
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = "box-barcode.pdf";
          link.click();
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);
        }
      } catch (error) {
        console.error("Print PDF error:", error);
        this.$notification.error({ message: "ບໍ່ສາມາດພິມ PDF ໄດ້" });
      }
    },

    /**
     * Handle box-barcode API error response
     * Handles pending sort confirmation errors and generic errors
     */
    handlePrintBarcodeError(response, error = null, chuteNumber = null) {
      // Handle API error response
      if (error && error.response && error.response.data) {
        const data = error.response.data;
        // Check for pending sort confirmation error
        if (data.message && typeof data.message === "object" && data.message.data) {
          const pendingPackages = data.message.data;
          const errorMsg = data.message.message || "ມີພັດສະດຸທີ່ຍັງບໍ່ໄດ້ຢືນຢັນການຄັດແຍກ";
          this.showPendingPackagesError(pendingPackages, errorMsg, chuteNumber);
          return;
        }
        // Generic error from response
        const msg = data.message && typeof data.message === "string"
          ? data.message
          : (data.message && data.message.message) || "ເກີດຂໍ້ຜິດພາດໃນການພິມ barcode";
        this.$notification.error({ message: msg });
        return;
      }

      // Handle non-200 success response with error format
      if (response && response.data && response.data.message && typeof response.data.message === "object") {
        const msgData = response.data.message;
        if (msgData.data && Array.isArray(msgData.data)) {
          const pendingPackages = msgData.data;
          const errorMsg = msgData.message || "ມີພັດສະດຸທີ່ຍັງບໍ່ໄດ້ຢືນຢັນການຄັດແຍກ";
          this.showPendingPackagesError(pendingPackages, errorMsg, chuteNumber);
          return;
        }
      }

      // Default error
      this.$notification.error({ message: "ບໍ່ສາມາດສ້າງ barcode ໄດ້" });
    },

    /**
     * Translate pending packages error message to Lao
     */
    translatePendingPackagesMessage(message) {
      if (!message) return "ມີພັດສະດຸທີ່ຍັງບໍ່ໄດ້ຢືນຢັນການຄັດແຍກ";

      // Match pattern: "Cannot generate label: X package(s) still pending sort confirmation..."
      const labelMatch = message.match(/Cannot generate label:\s*(\d+)\s*package\(s\)\s*still pending sort confirmation/i);
      if (labelMatch) {
        const count = labelMatch[1];
        return `ບໍ່ສາມາດສ້າງບິນປິນໄດ້: ມີ ${count} ພັດສະດຸທີ່ຍັງລໍຖ້າການຢືນຢັນການຄັດແຍກ. ກະລຸນາຢືນຢັນການຄັດແຍກກ່ອນພິມບິນ.`;
      }

      // Match pattern: "Cannot generate container: X package(s) still pending sort confirmation..."
      const containerMatch = message.match(/Cannot generate container:\s*(\d+)\s*package\(s\)\s*still pending sort confirmation/i);
      if (containerMatch) {
        const count = containerMatch[1];
        return `ບໍ່ສາມາດສ້າງບິນແພັກໄດ້: ມີ ${count} ພັດສະດຸທີ່ຍັງລໍຖ້າການຢືນຢັນການຄັດແຍກ. ກະລຸນາຢືນຢັນການຄັດແຍກກ່ອນສ້າງບິນແພັກ.`;
      }

      // Return original if no pattern matched
      return message;
    },

    /**
     * Show pending packages error dialog
     */
    showPendingPackagesError(packages, message, chuteNumber = null) {
      this.pendingPackagesError = {
        packages: packages,
        message: this.translatePendingPackagesMessage(message),
        chuteNumber: chuteNumber
      };
      this.showPendingPackagesDialog = true;
    },

    /**
     * Close pending packages error dialog
     */
    closePendingPackagesDialog() {
      this.showPendingPackagesDialog = false;
      this.pendingPackagesError = null;
    },

    /**
     * Handle skip pending packages success
     * Refreshes the dashboard and optionally triggers print for the original chute
     * @param {Object} data - Response data from skip API
     */
    async handleSkipPendingPackagesSuccess(data) {
      // Close dialog first
      this.closePendingPackagesDialog();

      // Refresh dashboard to update chute statuses
      await this.fetchDashboard();

      // Log the result for debugging
      console.log(`Skipped ${data.packagesMoved} packages to error chute ${data.errorChuteNumber}`);
    },

    /**
     * Open pending packages page in new tab
     * @param {string} status - Optional status filter (pending, sorting, or pending,sorting)
     */
    openPendingPackages(status = null, schedule = null) {
      if (!this.machineCode) return;

      const query = { machineCode: this.machineCode };
      if (status) {
        query.status = status;
      }
      if (this.selectedStatsDate) {
        query.date = this.selectedStatsDate;
      }
      if (schedule) {
        query.schedule = schedule;
      }

      const routeData = this.$router.resolve({
        name: 'distribution.center.sorting.pending.packages',
        query
      });
      window.open(routeData.href, '_blank');
    },

    /**
     * Open sorted packages page in new tab
     * @param {string} status - Optional status filter (sorted, packed, or all sorted statuses)
     * @param {string} schedule - Optional work schedule ('morning' | 'night')
     */
    openSortedPackages(status = null, schedule = null) {
      if (!this.machineCode) return;

      const query = { machineCode: this.machineCode };
      if (status) {
        query.status = status;
      }
      if (this.selectedStatsDate) {
        query.date = this.selectedStatsDate;
      }
      if (schedule) {
        query.schedule = schedule;
      }

      const routeData = this.$router.resolve({
        name: 'distribution.center.sorting.sorted.packages',
        query
      });
      window.open(routeData.href, '_blank');
    },

    /**
     * Open scanned packages (getapi666 scan log) page in new tab
     * @param {string} schedule - Optional schedule filter ('morning' | 'night' | null for all)
     */
    openScannedPackages(schedule = null) {
      if (!this.machineCode) return;

      const query = { machineCode: this.machineCode };
      if (this.selectedStatsDate) {
        query.date = this.selectedStatsDate;
      }
      if (schedule) {
        query.schedule = schedule;
      }

      const routeData = this.$router.resolve({
        name: 'distribution.center.sorting.scanned.packages',
        query
      });
      window.open(routeData.href, '_blank');
    },

    /**
     * Clear error chute
     * POST /api/sorting/admin/clear-error-chutes
     */
    async clearErrorChute(chuteNumber) {
      if (!this.machineCode || this.clearingChuteNumber !== null) return;

      this.clearingChuteNumber = chuteNumber;

      try {
        const payload = {
          machineCode: this.machineCode,
          chuteNumber: chuteNumber
        };

        const response = await this.$http.post("sorting/admin/clear-error-chutes", payload);

        if (response.data && (response.data.success || response.data.code === 200)) {
          this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);
          // Refresh dashboard to update chute status
          await this.fetchDashboard();
        } else {
          this.$notification.error({ message: "ບໍ່ສາມາດລ້າງຊ່ອງຂໍ້ຜິດພາດໄດ້" });
        }
      } catch (error) {
        console.error("Clear error chute error:", error);
        this.$notification.error({ message: "ເກີດຂໍ້ຜິດພາດໃນການລ້າງຊ່ອງ" });
      } finally {
        this.clearingChuteNumber = null;
      }
    },

    /**
     * Extract active chutes from response data
     * Handles multiple response formats:
     * Format 1: { data: { active_chutes: [...] }, error: "..." }
     * Format 2: { message: { message: "...", data: { active_chutes: [...] } } }
     * Format 3: { message: "...", data: { active_chutes: [...] } }
     */
    extractActiveChutes(responseData) {
      // Format 1 & 3: { data: { active_chutes: [...] }, error/message: "..." }
      if (responseData.data && responseData.data.active_chutes) {
        const errorMsg = responseData.error ||
          (typeof responseData.message === "string" ? responseData.message : null) ||
          "ມີຊ່ອງທີ່ຍັງມີພັດສະດຸຢູ່";
        return {
          message: errorMsg,
          chutes: responseData.data.active_chutes
        };
      }

      // Format 2: { message: { message: "...", data: { active_chutes: [...] } } }
      if (responseData.message && typeof responseData.message === "object") {
        const msgObj = responseData.message;
        if (msgObj.data && msgObj.data.active_chutes) {
          return {
            message: msgObj.message || "ມີຊ່ອງທີ່ຍັງມີພັດສະດຸຢູ່",
            chutes: msgObj.data.active_chutes
          };
        }
      }

      return null;
    },

    /**
     * Close active chutes dialog
     */
    closeActiveChutesDialog() {
      this.showActiveChutesDialog = false;
      this.activeChutesError = null;
    },

    /**
     * Print and remove chute from dialog (used in ActiveChutesErrorDialog)
     */
    async printActiveChute(chuteNumber) {
      if (!this.machineCode || this.printingChuteNumber !== null) return;

      this.printingChuteNumber = chuteNumber;

      try {
        const guid = this.generateBoxGuid();
        const payload = {
          guid: guid,
          machineCode: this.machineCode,
          operator: "",
          chuteCode: chuteNumber,
          containerBarcode: ""
        };

        const response = await this.$http.post("sorting/admin/box-barcode", payload);

        if (response.data && response.data.code === 200 && typeof response.data.message === "string") {
          // Print the base64 PDF
          this.printBase64Pdf(response.data.message);

          // Remove printed chute from the dialog list
          if (this.activeChutesError && this.activeChutesError.chutes) {
            this.activeChutesError.chutes = this.activeChutesError.chutes.filter(
              c => c.chute_number !== chuteNumber
            );
            // Close dialog if all chutes handled
            if (this.activeChutesError.chutes.length === 0) {
              this.closeActiveChutesDialog();
              // Refresh dashboard
              await this.fetchDashboard();
            }
          }
        } else {
          this.handlePrintBarcodeError(response, null, chuteNumber);
        }
      } catch (error) {
        console.error("Print barcode error:", error);
        this.handlePrintBarcodeError(null, error, chuteNumber);
      } finally {
        this.printingChuteNumber = null;
      }
    },

    /**
     * Clear a single active chute from the dialog
     */
    async clearActiveChute(chuteNumber) {
      if (!this.machineCode) return;

      try {
        const payload = {
          machineCode: this.machineCode,
          chuteNumber: chuteNumber
        };

        const response = await this.$http.post("sorting/admin/clear-error-chutes", payload);

        if (response.data && (response.data.success || response.data.code === 200)) {
          // Remove cleared chute from the list
          if (this.activeChutesError && this.activeChutesError.chutes) {
            this.activeChutesError.chutes = this.activeChutesError.chutes.filter(
              c => c.chute_number !== chuteNumber
            );
            // Close dialog if all chutes cleared
            if (this.activeChutesError.chutes.length === 0) {
              this.closeActiveChutesDialog();
              this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);
              // Refresh dashboard
              await this.fetchDashboard();
            }
          }
        } else {
          this.$notification.error({ message: "ບໍ່ສາມາດລ້າງຊ່ອງໄດ້" });
        }
      } catch (error) {
        console.error("Clear active chute error:", error);
        this.$notification.error({ message: "ເກີດຂໍ້ຜິດພາດໃນການລ້າງຊ່ອງ" });
      }
    },

    /**
     * Clear all active chutes from the dialog
     */
    async clearAllActiveChutes() {
      if (!this.machineCode || !this.activeChutesError || !this.activeChutesError.chutes) return;

      const chutes = [...this.activeChutesError.chutes];
      let clearedCount = 0;

      for (const chute of chutes) {
        try {
          const payload = {
            machineCode: this.machineCode,
            chuteNumber: chute.chute_number
          };

          const response = await this.$http.post("sorting/admin/clear-error-chutes", payload);

          if (response.data && (response.data.success || response.data.code === 200)) {
            clearedCount++;
            // Remove from list
            if (this.activeChutesError && this.activeChutesError.chutes) {
              this.activeChutesError.chutes = this.activeChutesError.chutes.filter(
                c => c.chute_number !== chute.chute_number
              );
            }
          }
        } catch (error) {
          console.error(`Failed to clear chute ${chute.chute_number}:`, error);
        }
      }

      if (clearedCount > 0) {
        this.$notification.OpenNotification_EditItem_OnSuccess("top-right", "dark", 3000);
        // Refresh dashboard
        await this.fetchDashboard();
      }

      // Close dialog if all cleared
      if (!this.activeChutesError || !this.activeChutesError.chutes || this.activeChutesError.chutes.length === 0) {
        this.closeActiveChutesDialog();
      }
    }
  },

  beforeDestroy() {
    // Clean up intervals when component is destroyed
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
    this.stopLatencyProbe();
  },

  watch: {
    "machineCode": function(newVal) {
      if (newVal) {
        this.loadMachineData();
      } else {
        this.currentMode = null;
        this.modeStatus = null;
        this.chuteMappings = [];
        this.districts = [];
        // Clear dashboard data and stop auto-refresh
        this.dashboard = null;
        this.dashboardChutes = [];
        this.chutesSummary = null;
        this.todayStats = null;
        this.dashboardAlerts = [];
        this.dashboardStats = null;
        this.stopAutoRefresh();
      }
    },

    "selectedMode": function(newVal) {
      this.clearErrors();
      if (newVal === "branch" && this.districts.length === 0) {
        this.getDistricts();
      }
    }
  }
};
